// @ts-check

/**
 * @typedef {{row: HTMLTableRowElement, primaryId: string}} ProductReference
 * @typedef {Map<string, ProductReference>} ProductMap
 * @typedef {'accessory' | 'article'} ProductType
 */

class HttpError extends Error {
  /** HTTP error code, which will be communicated to the client */
  statusCode;

  /**
   * @param message {string} - Mesasge describing the error in detail
   * @param statusCode {number} - Http status code to send with the repsonse
   */
  constructor(message, statusCode) {
    super(message);
    this.statusCode = statusCode;
  }
}

class InternalArticleInfosExpander {
  /**
   * Fetch internal infos for given chunk of product ids
   * @param {string[]} chunk
   * @param {'accessory' | 'article'} type
   */
  static async #fetchChunk(chunk, type) {
    const pluralType = type === 'accessory' ? 'accessories' : 'articles';
    const url = `${confEnv.pdb_api_url}/internalproductdata/batch/${k3vars.S_Sprache}/${pluralType}?api=v1.1&ids=${chunk.join(',')}`;

    try {
      const response = await fetch(url, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new HttpError(
          `Failed to fetch internal article info html: ${response.statusText}`,
          response.status,
        );
      }

      const internalInfos = await response.json();

      return internalInfos;
    } catch (error) {
      if (error instanceof HttpError && error.statusCode === 403) {
        // If the user is not allowed to see infos just suppress the error
        return {};
      }

      throw error;
    }
  }

  /**
   * Apply an empty error state to all found columns
   * @param {ProductReference} product
   */
  static #applyErrorState(product) {
    const columnSelectors = [
      '[data-myerco-product-price]',
      '[data-myerco-delivery-time]',
      '[data-myerco-product-availability]',
    ];
    columnSelectors.forEach((selector) => {
      const column = product.row.querySelector(selector);
      if (column) {
        column.textContent = '-';
      }
    });
  }

  /**
   * Apply fetched product infos to the row of given product
   * @param {ProductReference} product
   * @param {any} internalInfos
   */
  static #applyInternalInfos(product, internalInfos) {
    if (!internalInfos) {
      return;
    }

    if (internalInfos.can_see_price && internalInfos.formattedPrice !== '') {
      let price =
        internalInfos.currency === 'CHF'
          ? ` ${internalInfos.formattedPrice} `
          : `${internalInfos.formattedPrice} `;
          
      if (internalInfos.price === '' || Number(internalInfos.price) === 0) {
        price = '-';
      }

      const priceColumn = product.row.querySelector(
        '[data-myerco-product-price]',
      );
      if (priceColumn) {
        priceColumn.textContent = price;
        priceColumn.classList.add(`is-${internalInfos.currency}-currency`);
      }
    }

    if (
      internalInfos.can_see_delivery_times &&
      internalInfos.delivery_in_days !== null
    ) {
      const deliveryTimeColumn = product.row.querySelector(
        '[data-myerco-delivery-time]',
      );
      if (!deliveryTimeColumn) {
        return;
      }

      const deliveryTimeText =
        internalInfos.delivery_in_days === 1
          ? `${globals.trans.myerco.label_ein_tag.replace('%s', internalInfos.delivery_in_days)}`
          : `${globals.trans.myerco.label_x_tage.replace('%s', internalInfos.delivery_in_days)}`;
      deliveryTimeColumn.textContent = deliveryTimeText;
    }

    if (
      internalInfos.can_see_availability &&
      (internalInfos.bookable !== null ||
        internalInfos.available_timestamp_in_seconds !== null)
    ) {
      const availabilityColumn = product.row.querySelector(
        '[data-myerco-product-availability]',
      );
      if (!availabilityColumn) {
        return;
      }

      if (internalInfos.bookable) {
        availabilityColumn.innerHTML = '<span class="is-available"></span>';
        return;
      }

      const notAvailableSpan = document.createElement('span');
      notAvailableSpan.classList.add('is-not-available');

      if (
        internalInfos.available_timestamp_in_seconds &&
        internalInfos.available_timestamp_is_future_date
      ) {
        const availableDate = new Date(
          internalInfos.available_timestamp_in_seconds,
        );

        notAvailableSpan.innerText = availableDate.toLocaleDateString(
          k3vars.S_Sprache,
          {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          },
        );
      }

      // replace content of availability column with not available span
      availabilityColumn.innerHTML = '';
      availabilityColumn.appendChild(notAvailableSpan);
    }
  }

  /**
   * Fetch and apply internal infos for given products in chunks to allow fast
   * initial rendering and then filling in infos as they arrive
   * @param {ProductMap} productMap
   * @param {ProductType} productType
   * @param {number} chunkSize
   * @param {Promise<void>} [translationPromise]
   */
  static #queueProductInfosExpansion(
    productMap,
    productType,
    chunkSize,
    translationPromise,
  ) {
    let promiseChain = Promise.resolve();
    const productChunks = Math.ceil(productMap.size / chunkSize);
    const productArray = Array.from(productMap.entries());

    for (let i = 0; i < productChunks; i++) {
      const chunk = productArray.slice(i * chunkSize, (i + 1) * chunkSize);

      promiseChain = promiseChain
        .then(() =>
          InternalArticleInfosExpander.#fetchChunk(
            chunk.map(([productId]) => productId),
            productType,
          ),
        )
        .then((internalInfos) => {
          (translationPromise ?? Promise.resolve()).then(() => {
            chunk.forEach(([_, product]) => {
              const internalInfo = internalInfos[product.primaryId];

              if (internalInfo) {
                InternalArticleInfosExpander.#applyInternalInfos(
                  product,
                  internalInfo,
                );
              } else {
                InternalArticleInfosExpander.#applyErrorState(product);
              }
            });
          });
        })
        .catch((error) => {
          console.error(`Error while expanding internal infos`);
          console.error(error);

          chunk.forEach(([_, product]) => {
            InternalArticleInfosExpander.#applyErrorState(product);
          });
        });
    }

    return promiseChain;
  }

  /**
   * Expand found products in myerco tables with internal article infos.
   * @param {Promise<void> | undefined} translationPromise
   * @param {number} [chunkSize=5]
   */
  static async expandTables(translationPromise = undefined, chunkSize = 14) {
    if (!(await authHelper.isLoggedIn)) {
      // Don't even try to fetch infos if user is not logged in, as we will get
      // 403 errors for all requests. Also infos are only relevant for logged in
      // users, so no need to fetch them for anonymous users.
      return;
    }

    /** @type {ProductMap} */
    const articles = new Map();
    /** @type {ProductMap} */
    const accessories = new Map();
    /** @type {NodeListOf<HTMLTableRowElement>} */
    const rawRows = document.querySelectorAll(
      '[data-articles] tr[data-collectable-id]',
    );
    Array.from(rawRows).forEach((row) => {
      const productId = row.getAttribute('data-collectable-id');
      const primaryId = row.getAttribute('data-myerco-primary-id');
      const productType = row.getAttribute('data-myerco-product-type');

      if (!productId || !primaryId || !productType) {
        return;
      }

      if (productType === 'accessory') {
        accessories.set(productId, {
          row,
          primaryId,
        });
        return;
      }

      articles.set(productId, {
        row,
        primaryId,
      });
    });

    const articlePromiseChain =
      InternalArticleInfosExpander.#queueProductInfosExpansion(
        articles,
        'article',
        chunkSize,
        translationPromise,
      );
    const accessoryPromiseChain =
      InternalArticleInfosExpander.#queueProductInfosExpansion(
        accessories,
        'accessory',
        chunkSize,
        translationPromise,
      );

    return Promise.allSettled([articlePromiseChain, accessoryPromiseChain]);
  }
}
